import React, { useState, useEffect, useMemo } from "react";
import AdminSearchTable from "../Admin/AdminSearchTable";
import FilterIcon from '../../SVG/Filter';
import { useAuth } from "../../src/Context/AuthContext"
import { useNavigate } from 'react-router-dom';

const LoginAttemptsSection = () => {
    // State Variables
    const [loginAttempts, setLoginAttempts] = useState([]);
    const [loading, setLoading] = useState(false);
    const { logout } = useAuth();
    const navigate = useNavigate();

    // Filters
    const [filters, setFilters] = useState({
        username: "",
        startDate: "",
        endDate: "",
        success: "",
    });

    // Unique dropdown values
    const [dropdownOptions, setDropdownOptions] = useState({
        success: [],
    });

    // ENV Variables
    const apiURL = import.meta.env.VITE_API;

    //Data Fetch On Load
    useEffect(() => {
        const fetchLoginAttempts = async () => {
            setLoading(true);
            try {
                const res = await fetch(`${apiURL}/admin/search/loginattempts`, {
                    method: "GET",
                    credentials: "include",
                });

                if (res.status === 401) {
                    logout();
                    navigate('/login');
                    throw new Error("unauthorized");
                }

                if (res.status === 403) {
                    navigate('/home');
                    throw new Error("forbidden");
                }

                if (!res.ok) {
                    throw new Error();
                }

                const data = await res.json();
                setLoginAttempts(data || []);

                // Collect dropdown options for success
                const successOptions = [
                    ...new Set(data.map((x) => x.success).filter(Boolean)),
                ];

                setDropdownOptions({ success: successOptions });
            } catch (error) {
                console.error("Error fetching login attempts:", error);
                alert("Error fetching login attempts.");
            } finally {
                setLoading(false);
            }
        };

        fetchLoginAttempts();
    }, []); 


    const filteredData = useMemo(() => {
        return loginAttempts.filter((attempt) => {
            if (
                filters.username &&
                !attempt.userName
                    ?.toLowerCase()
                    .includes(filters.username.toLowerCase())
            )
                return false;

            if (filters.success && attempt.success !== filters.success)
                return false;

            if (filters.startDate) {
                const start = new Date(filters.startDate);
                const attemptDate = new Date(attempt.transactionDate);
                if (attemptDate < start) return false;
            }

            if (filters.endDate) {
                const end = new Date(filters.endDate);
                const attemptDate = new Date(attempt.transactionDate);
                if (attemptDate > end) return false;
            }

            return true;
        });
    }, [loginAttempts, filters]);

    // Table columns
    const columns = [
        { label: "Username", accessor: "userName" },
        { label: "Transaction Date", accessor: "transactionDate" },
        { label: "Failure Reason", accessor: "errorDescription" },
        { label: "Success", accessor: "success" },
    ];

    return (
        <div className="admin-materials-page">
                <form className="filter-box">
                    <FilterIcon />

                    <div className="filter-item">
                        <label className="filterLabel">Username</label>
                        <input
                            type="text"
                            className="filterInput"
                            placeholder="Filter by username"
                            value={filters.username}
                            onChange={(e) =>
                                setFilters({
                                    ...filters,
                                    username: e.target.value,
                                })
                            }
                        />
                    </div>

                    <div className="filter-item">
                        <label className="filterLabel">Start Date</label>
                        <input
                            type="date"
                            className="filterInput"
                            value={filters.startDate}
                            onChange={(e) =>
                                setFilters({
                                    ...filters,
                                    startDate: e.target.value,
                                })
                            }
                        />
                    </div>

                    <div className="filter-item">
                        <label className="filterLabel">End Date</label>
                        <input
                            type="date"
                            className="filterInput"
                            value={filters.endDate}
                            onChange={(e) =>
                                setFilters({
                                    ...filters,
                                    endDate: e.target.value,
                                })
                            }
                        />
                    </div>

                    <div className="filter-item">
                        <label className="filterLabel">Status</label>
                        <select
                            className="baseDropDown"
                            value={filters.success}
                            onChange={(e) =>
                                setFilters({
                                    ...filters,
                                    success: e.target.value,
                                })
                            }
                        >
                            <option value="">All Statuses</option>
                            {dropdownOptions.success.map((s) => (
                                <option key={s} value={s}>
                                    {s}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="filter-button-box">
                        <button
                            type="button"
                            className="filterButton"
                            onClick={() =>
                                setFilters({
                                    username: "",
                                    startDate: "",
                                    endDate: "",
                                    success: "",
                                })
                            }
                        >
                            Clear Filters
                        </button>
                    </div>
                </form>

            {/* Table or loading */}
            {loading ? (
                <div>Loading...</div>
            ) : (
                <AdminSearchTable columns={columns} data={filteredData} />
            )}
        </div>
    );
};

export default LoginAttemptsSection;
